// Public sample dataset for the read-only Sample Board demo. No backend, no writes.
// Deterministic data designed to populate every core module so prospects see a
// realistic, lived-in workspace.
const DAY = 86400000;
const rel = (days) => new Date(Date.now() + days * DAY).toISOString();
const hrs = (h) => new Date(Date.now() + h * 3600000).toISOString();
// A fixed clock-time today/yesterday (e.g. "today at 9:20") for friendly labels.
const atToday = (h, m = 0) => { const d = new Date(); d.setHours(h, m, 0, 0); return d.toISOString(); };
const atYesterday = (h, m = 0) => { const d = new Date(Date.now() - DAY); d.setHours(h, m, 0, 0); return d.toISOString(); };

export const DEMO_USER = "Priya Nair";
const OWNERS = ["Priya Nair", "Marcus Reyes", "Dana Whitfield"];

export const SAMPLE_STAGES = [
  "New Job", "Pickup Scheduled", "Picked Up", "In Transit", "At Facility",
  "Ready for Delivery", "Delivered", "Ready to Close", "Closed",
];

const DELIVERED_STAGES = ["Delivered", "Ready to Close", "Closed"];

function defaultDocs(stage, over = {}) {
  const delivered = DELIVERED_STAGES.includes(stage);
  const base = {
    "Bill of Lading": "Received",
    "Commercial Invoice": "Received",
    "Packing List": "Received",
    "Proof of Delivery": delivered ? "Uploaded" : "Not Required",
    "Customer Authorization": "Not Required",
    ...over,
  };
  return Object.entries(base).map(([type, status]) => ({ type, status }));
}

function autoChecklist(stage, docs, blockers) {
  const delivered = DELIVERED_STAGES.includes(stage);
  const pod = docs.find((d) => d.type === "Proof of Delivery");
  const podOk = pod && ["Received", "Uploaded"].includes(pod.status);
  const bl = (blockers || []).map((b) => b.name);
  const finalReview = stage === "Ready to Close" || stage === "Closed";
  return {
    proof_of_delivery_received: !!(delivered && podOk),
    final_customer_update_sent: delivered && !bl.includes("Final customer update sent"),
    required_documents_complete: !docs.some((d) => d.status === "Missing"),
    internal_notes_complete: true,
    billing_handoff_complete: finalReview && !bl.includes("Billing handoff complete"),
    manager_review_complete: finalReview && !bl.includes("Manager review complete"),
  };
}

function genTimeline(o, stage, owner) {
  const idx = SAMPLE_STAGES.indexOf(stage);
  const t = [{ id: o.job + "-t0", type: "job_created", message: "Job created", actor: owner, timestamp: rel(-(idx + 3)) }];
  SAMPLE_STAGES.slice(1, idx + 1).forEach((s, k) => {
    t.push({ id: o.job + "-t" + (k + 1), type: "stage_change", message: `Stage moved to ${s}`, actor: owner, timestamp: hrs(-(48 - k * 6)) });
  });
  if (stage === "Closed") t.push({ id: o.job + "-tc", type: "job_closed", message: "Job closed", actor: owner, timestamp: hrs(-8) });
  return t;
}

function genNotes(o, owner) {
  return o.notes || [
    { id: o.job + "-n1", body: o.note || "Coordinating with carrier and customer; tracking documents and delivery schedule.", author: owner, created_at: hrs(-14) },
  ];
}

function defaultUpdateStatus(stage, o) {
  if ((o.issues || []).includes("Customer update overdue")) return "Update overdue";
  if (stage === "Closed") return "Closed";
  if (stage === "Ready to Close") return o.can_close ? "Updated" : "Final update required";
  if (stage === "Delivered") return "Delivered — confirmation pending";
  if (["New Job", "Pickup Scheduled"].includes(stage)) return "Awaiting pickup";
  return "Updated";
}

function mk(o) {
  const stage = o.stage;
  const closed = stage === "Closed";
  const rtc = stage === "Ready to Close";
  const owner = o.owner;
  const docs = o.docs || defaultDocs(stage);
  const updates = o.updates || [];
  const checklist = o.checklist || autoChecklist(stage, docs, o.close_blockers);
  return {
    id: "sample-" + o.job,
    job_number: o.job,
    customer_name: o.customer,
    pickup_location: o.pickup,
    delivery_location: o.delivery,
    current_stage: stage,
    risk_status: o.risk || "On Track",
    assigned_owner: owner,
    next_action: o.next_action || (closed ? "Job closed" : rtc ? "Complete closeout review" : "Advance to next stage"),
    next_action_due_date: o.na_due !== undefined ? o.na_due : (closed ? null : rel(2)),
    next_action_state: o.na_state || (o.na_overdue ? "Overdue" : (closed ? "Completed" : "Open")),
    customer_update_due_date: o.update_due !== undefined ? o.update_due : null,
    delivery_due_date: closed ? rel(-3) : rel(2 + (SAMPLE_STAGES.indexOf(stage) % 4)),
    last_updated_at: o.last_updated || hrs(-5),
    issues: o.issues || [],
    documents: docs,
    customer_updates: updates,
    can_close: rtc ? !!o.can_close : false,
    rtc_blockers: o.rtc_blockers || [],
    close_blockers: o.close_blockers || [],
    closeout_checklist: checklist,
    internal_notes: genNotes(o, owner),
    timeline: genTimeline(o, stage, owner),
    closed_at: closed ? hrs(-8) : null,
    closed_by: closed ? owner : null,
    closeout_note: o.closeout_note || (closed ? "Delivered, POD on file, billing handed off. Closed clean." : ""),
    // Customer Updates screen fields
    update_status: o.update_status || defaultUpdateStatus(stage, o),
    last_update_sent: o.last_update_sent !== undefined ? o.last_update_sent : (updates.length ? updates[updates.length - 1].sent_at : null),
    next_update_due: o.next_update_due || (closed ? "Not due" : "Tomorrow"),
    suggested_update: o.suggested_update !== undefined ? o.suggested_update : null,
  };
}

const upd = (job, msg, by, sent_at, status) => ({ id: job + "-u" + Math.random().toString(36).slice(2, 6), message: msg, customer_facing_status: status, sent_by: by, sent_at });

// ---------------------------------------------------------------------------
// 30 jobs (24 active + 6 closed). Counts are tuned to match the Today metrics:
// Active 24 · Needs Attention 8 · Missing Docs 4 · Updates Due 5 · RTC 3 ·
// Closeout Blocked 2 · Closed 6
// ---------------------------------------------------------------------------
const SPECS = [
  // ---- 8 NEEDS-ATTENTION ACTIVE JOBS ----
  mk({
    job: "SB-2001", customer: "Cedarline Manufacturing", pickup: "Cleveland, OH", delivery: "Columbus, OH",
    stage: "In Transit", owner: "Marcus Reyes", issues: ["Customer update overdue"],
    update_due: rel(0), last_updated: atYesterday(15, 15),
    update_status: "Update overdue", last_update_sent: atYesterday(15, 15), next_update_due: "Today",
    suggested_update: "Your shipment is currently in transit. We are monitoring the delivery schedule and will send the next update once the carrier confirms arrival.",
    updates: [upd("SB-2001", "Your shipment has been picked up and is now in transit.", "Marcus Reyes", atYesterday(15, 15), "In Transit")],
  }),
  mk({
    job: "SB-2002", customer: "Brightfield Organics", pickup: "Fresno, CA", delivery: "Sacramento, CA",
    stage: "Picked Up", owner: "Priya Nair",
    issues: ["Required document missing: Bill of Lading", "Required document missing: Commercial Invoice", "Customer update overdue"],
    docs: defaultDocs("Picked Up", { "Bill of Lading": "Missing", "Commercial Invoice": "Missing" }),
    update_due: hrs(-1), last_updated: hrs(-20),
    update_status: "Missing document update needed", last_update_sent: hrs(-20), next_update_due: "Now",
    suggested_update: "We are waiting on the Bill of Lading and Commercial Invoice before the shipment can move to the next step. We will update you once those documents are received.",
    updates: [upd("SB-2002", "Your shipment has been collected by the carrier.", "Priya Nair", hrs(-20), "Picked Up")],
  }),
  mk({
    job: "SB-2003", customer: "Harbor Foods", pickup: "Seattle, WA", delivery: "Portland, OR",
    stage: "Pickup Scheduled", owner: "Priya Nair", issues: ["Next action overdue"],
    next_action: "Confirm carrier pickup window", na_due: rel(-1), na_overdue: true, na_state: "Overdue", last_updated: hrs(-26),
    update_status: "Awaiting pickup", last_update_sent: rel(-2), next_update_due: "Tomorrow",
    suggested_update: "We're confirming your carrier pickup window and will notify you as soon as collection is scheduled.",
    updates: [upd("SB-2003", "We have received your shipment request and are scheduling pickup.", "Priya Nair", rel(-2), "Pickup Scheduled")],
  }),
  mk({
    job: "SB-2005", customer: "Summit Auto Parts", pickup: "Detroit, MI", delivery: "Chicago, IL",
    stage: "Ready for Delivery", owner: "Priya Nair", issues: ["Customer update overdue"],
    update_due: rel(0), last_updated: hrs(-22),
    update_status: "Update due — delivery scheduled", last_update_sent: hrs(-22), next_update_due: "Today",
    suggested_update: "Delivery has been scheduled for tomorrow. We will send confirmation once the shipment is completed.",
    updates: [upd("SB-2005", "Your shipment has arrived and is ready for delivery scheduling.", "Priya Nair", hrs(-22), "Ready for Delivery")],
  }),
  mk({
    job: "SB-2006", customer: "Ironwood Building Materials", pickup: "Dallas, TX", delivery: "Houston, TX",
    stage: "At Facility", owner: "Priya Nair", issues: ["Required document missing: Packing List"],
    docs: defaultDocs("At Facility", { "Packing List": "Missing" }), last_updated: hrs(-9),
    update_status: "At facility", last_update_sent: hrs(-9), next_update_due: "Tomorrow",
    suggested_update: "Your shipment is at our facility and being prepared for the final delivery leg.",
    updates: [upd("SB-2006", "Your shipment has arrived at our destination facility.", "Priya Nair", hrs(-9), "At Facility")],
  }),
  mk({
    job: "SB-2008", customer: "Solstice Beverage Co.", pickup: "Denver, CO", delivery: "Salt Lake City, UT",
    stage: "In Transit", owner: "Marcus Reyes", risk: "Delayed", issues: ["Shipment delayed", "Customer update overdue"],
    next_action: "Call carrier for updated ETA", na_due: rel(-1), na_overdue: true, na_state: "Overdue",
    update_due: hrs(-2), last_updated: hrs(-30),
    update_status: "Update overdue — delayed", last_update_sent: rel(-1), next_update_due: "Now",
    suggested_update: "Your shipment is delayed in transit. We are coordinating with the carrier for an updated ETA and will keep you posted.",
    updates: [upd("SB-2008", "Your shipment is in transit on its scheduled route.", "Marcus Reyes", rel(-1), "In Transit")],
  }),
  mk({
    job: "SB-2018", customer: "Vantage Medical Supply", pickup: "Raleigh, NC", delivery: "Richmond, VA",
    stage: "Delivered", owner: "Marcus Reyes", issues: ["Proof of delivery missing"],
    docs: defaultDocs("Delivered", { "Proof of Delivery": "Missing" }), last_updated: hrs(-8),
    update_status: "Delivered — POD pending", last_update_sent: hrs(-8), next_update_due: "Now",
    suggested_update: "Your shipment has been delivered. We are collecting the signed proof of delivery to complete closeout.",
    updates: [upd("SB-2018", "Your shipment has been delivered.", "Marcus Reyes", hrs(-8), "Delivered")],
  }),
  mk({
    job: "SB-2020", customer: "Northwind Apparel", pickup: "Trenton, NJ", delivery: "Hartford, CT",
    stage: "Delivered", owner: "Dana Whitfield", issues: ["Required document missing: Commercial Invoice", "Customer update overdue"],
    docs: defaultDocs("Delivered", { "Commercial Invoice": "Missing" }), update_due: hrs(-3), last_updated: hrs(-26),
    update_status: "Update overdue", last_update_sent: rel(-1), next_update_due: "Now",
    suggested_update: "Your shipment has been delivered. We are finalizing documentation before closeout and will confirm shortly.",
    updates: [upd("SB-2020", "Your shipment has been delivered and is awaiting document finalization.", "Dana Whitfield", rel(-1), "Delivered")],
  }),

  // ---- 3 READY TO CLOSE (2 blocked, 1 ready) ----
  mk({
    job: "SB-2011", customer: "Vantage Medical Supply", pickup: "Atlanta, GA", delivery: "Charlotte, NC",
    stage: "Ready to Close", owner: "Priya Nair", can_close: false,
    close_blockers: [{ name: "Final customer update sent", status: "Not sent" }],
    next_action: "Send final customer update", last_updated: hrs(-26),
    update_status: "Final update required", last_update_sent: rel(-1), next_update_due: "Now",
    suggested_update: "Your shipment has been delivered and is now in final closeout. We will send the final confirmation once the proof of delivery and closeout review are complete.",
    updates: [upd("SB-2011", "Your shipment has been delivered.", "Priya Nair", rel(-1), "Delivered")],
  }),
  mk({
    job: "SB-2012", customer: "Cedarline Manufacturing", pickup: "Columbus, OH", delivery: "Toledo, OH",
    stage: "Ready to Close", owner: "Marcus Reyes", can_close: false,
    close_blockers: [
      { name: "Manager review complete", status: "Incomplete", owner: "Marcus Reyes" },
      { name: "Billing handoff complete", status: "Incomplete", owner: "Marcus Reyes" },
    ],
    next_action: "Complete manager review & billing handoff", last_updated: hrs(-18),
    update_status: "Awaiting closeout review", last_update_sent: rel(-1), next_update_due: "Not due",
    updates: [upd("SB-2012", "Your shipment has been delivered. We are completing final closeout.", "Marcus Reyes", rel(-1), "Delivered")],
  }),
  mk({
    job: "SB-2014", customer: "Ironwood Building Materials", pickup: "Houston, TX", delivery: "San Antonio, TX",
    stage: "Ready to Close", owner: "Priya Nair", can_close: true,
    next_action: "Mark as Closed", last_updated: atToday(9, 20),
    update_status: "Updated", last_update_sent: atToday(9, 20), next_update_due: "Not due", suggested_update: null,
    updates: [upd("SB-2014", "Your shipment has been delivered and the proof of delivery is on file. Final closeout is complete.", "Priya Nair", atToday(9, 20), "Delivered")],
  }),

  // ---- 13 HEALTHY ACTIVE JOBS (On Track, across stages) ----
  mk({ job: "SB-2004", customer: "Northwind Apparel", pickup: "Newark, NJ", delivery: "Boston, MA", stage: "New Job", owner: "Priya Nair",
    docs: defaultDocs("New Job", { "Commercial Invoice": "Pending" }),
    update_status: "New job received", next_update_due: "Tomorrow",
    suggested_update: "We have received your shipment request and are preparing the job.",
    updates: [upd("SB-2004", "We have received your shipment request and are preparing the job.", "Priya Nair", hrs(-3), "New Job")] }),
  mk({ job: "SB-2007", customer: "Vantage Medical Supply", pickup: "Atlanta, GA", delivery: "Birmingham, AL", stage: "Pickup Scheduled", owner: "Marcus Reyes",
    docs: defaultDocs("Pickup Scheduled", { "Commercial Invoice": "Pending", "Packing List": "Pending" }),
    updates: [upd("SB-2007", "Your shipment pickup has been scheduled.", "Marcus Reyes", hrs(-7), "Pickup Scheduled")] }),
  mk({ job: "SB-2009", customer: "Cedarline Manufacturing", pickup: "Cleveland, OH", delivery: "Pittsburgh, PA", stage: "Picked Up", owner: "Dana Whitfield",
    updates: [upd("SB-2009", "Your shipment has been collected by the carrier.", "Dana Whitfield", hrs(-10), "Picked Up")] }),
  mk({ job: "SB-2010", customer: "Harbor Foods", pickup: "Tacoma, WA", delivery: "Spokane, WA", stage: "In Transit", owner: "Priya Nair",
    updates: [upd("SB-2010", "Your shipment is in transit and on its scheduled route.", "Priya Nair", hrs(-6), "In Transit")] }),
  mk({ job: "SB-2013", customer: "Summit Auto Parts", pickup: "Detroit, MI", delivery: "Indianapolis, IN", stage: "In Transit", owner: "Marcus Reyes",
    updates: [upd("SB-2013", "Your shipment is in transit and on schedule.", "Marcus Reyes", hrs(-9), "In Transit")] }),
  mk({ job: "SB-2015", customer: "Solstice Beverage Co.", pickup: "Denver, CO", delivery: "Albuquerque, NM", stage: "At Facility", owner: "Dana Whitfield",
    updates: [upd("SB-2015", "Your shipment has arrived at our destination facility.", "Dana Whitfield", hrs(-11), "At Facility")] }),
  mk({ job: "SB-2016", customer: "Brightfield Organics", pickup: "Fresno, CA", delivery: "Los Angeles, CA", stage: "At Facility", owner: "Priya Nair",
    updates: [upd("SB-2016", "Your shipment is at our facility and being prepared for delivery.", "Priya Nair", hrs(-8), "At Facility")] }),
  mk({ job: "SB-2017", customer: "Cedarline Manufacturing", pickup: "Akron, OH", delivery: "Cincinnati, OH", stage: "Ready for Delivery", owner: "Marcus Reyes",
    updates: [upd("SB-2017", "Your shipment is ready for delivery. We are coordinating the appointment.", "Marcus Reyes", hrs(-5), "Ready for Delivery")] }),
  mk({ job: "SB-2019", customer: "Harbor Foods", pickup: "Seattle, WA", delivery: "Bellevue, WA", stage: "Ready for Delivery", owner: "Dana Whitfield",
    updates: [upd("SB-2019", "Your shipment is ready for delivery.", "Dana Whitfield", hrs(-4), "Ready for Delivery")] }),
  mk({ job: "SB-2021", customer: "Ironwood Building Materials", pickup: "Houston, TX", delivery: "Austin, TX", stage: "Delivered", owner: "Priya Nair",
    update_status: "Delivered", next_update_due: "Tomorrow",
    updates: [upd("SB-2021", "Your shipment has been delivered. Proof of delivery is on file.", "Priya Nair", hrs(-7), "Delivered")] }),
  mk({ job: "SB-2022", customer: "Summit Auto Parts", pickup: "Chicago, IL", delivery: "Milwaukee, WI", stage: "Delivered", owner: "Marcus Reyes",
    update_status: "Delivered", next_update_due: "Tomorrow",
    updates: [upd("SB-2022", "Your shipment has been delivered.", "Marcus Reyes", hrs(-9), "Delivered")] }),
  mk({ job: "SB-2023", customer: "Northwind Apparel", pickup: "Newark, NJ", delivery: "Philadelphia, PA", stage: "Picked Up", owner: "Dana Whitfield",
    updates: [upd("SB-2023", "Your shipment has been collected and is moving to transit.", "Dana Whitfield", hrs(-12), "Picked Up")] }),
  mk({ job: "SB-2024", customer: "Solstice Beverage Co.", pickup: "Salt Lake City, UT", delivery: "Boise, ID", stage: "In Transit", owner: "Marcus Reyes",
    updates: [upd("SB-2024", "Your shipment is in transit and on schedule.", "Marcus Reyes", hrs(-6), "In Transit")] }),

  // ---- 6 CLOSED JOBS ----
  mk({ job: "SB-2025", customer: "Harbor Foods", pickup: "Portland, OR", delivery: "Eugene, OR", stage: "Closed", owner: "Priya Nair",
    closeout_note: "Delivered on schedule. POD on file. Billing handed off.",
    update_status: "Closed", next_update_due: "Not due",
    updates: [upd("SB-2025", "Your shipment has been delivered and the job is now closed. Thank you for working with us.", "Priya Nair", hrs(-10), "Closed")] }),
  mk({ job: "SB-2026", customer: "Northwind Apparel", pickup: "Boston, MA", delivery: "Providence, RI", stage: "Closed", owner: "Marcus Reyes",
    closeout_note: "Clean closeout, customer confirmed receipt.",
    update_status: "Closed", next_update_due: "Not due",
    updates: [upd("SB-2026", "This shipment has been completed and closed.", "Marcus Reyes", hrs(-14), "Closed")] }),
  mk({ job: "SB-2027", customer: "Summit Auto Parts", pickup: "Indianapolis, IN", delivery: "Louisville, KY", stage: "Closed", owner: "Dana Whitfield",
    closeout_note: "All documents complete, no exceptions.",
    update_status: "Closed", next_update_due: "Not due",
    updates: [upd("SB-2027", "Your shipment has been completed and closed.", "Dana Whitfield", hrs(-20), "Closed")] }),
  mk({ job: "SB-2028", customer: "Cedarline Manufacturing", pickup: "Toledo, OH", delivery: "Detroit, MI", stage: "Closed", owner: "Priya Nair",
    closeout_note: "Delivered early. Customer notified and satisfied.",
    update_status: "Closed", next_update_due: "Not due",
    updates: [upd("SB-2028", "Your shipment has been completed and closed.", "Priya Nair", hrs(-26), "Closed")] }),
  mk({ job: "SB-2029", customer: "Vantage Medical Supply", pickup: "Charlotte, NC", delivery: "Columbia, SC", stage: "Closed", owner: "Marcus Reyes",
    closeout_note: "Closeout reviewed by manager. Archived.",
    update_status: "Closed", next_update_due: "Not due",
    updates: [upd("SB-2029", "This shipment has been completed and closed.", "Marcus Reyes", hrs(-30), "Closed")] }),
  mk({ job: "SB-2030", customer: "Brightfield Organics", pickup: "Los Angeles, CA", delivery: "San Diego, CA", stage: "Closed", owner: "Dana Whitfield",
    closeout_note: "Final invoice issued. Job closed.",
    update_status: "Closed", next_update_due: "Not due",
    updates: [upd("SB-2030", "Your shipment has been completed and closed. Thank you.", "Dana Whitfield", hrs(-34), "Closed")] }),
];

export const SAMPLE_JOBS = SPECS;
export const SAMPLE_OWNERS = OWNERS;
